import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { Store } from "@ngrx/store";
import { AppService } from "src/app/app.service";
import { PageParams } from "src/app/store/models/navigation-settings.model";
import {
  HighLow,
  Revisions,
  Surprise,
  Variation,
  Visualizations,
} from "src/app/store/models/right-panel-models";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { initialState } from "../../store/reducers/right-panel-reducer";

declare function gridRefresh(eventTarget, event);
declare function OnDefaultSetting(sectionName);
declare function variationOrRevisionSliderMoved(cell, obj);
declare function getDivisionFactorForSurprise(obj);
declare function surpriseColoring(
  cell,
  surpriseValue,
  divisionFactor,
  isSliderChange,
);
declare function highLowFilter(property, isChecked);

@Component({
  selector: "app-visualizations",
  templateUrl: "./visualizations.component.html",
  styleUrls: ["./visualizations.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class VisualizationsComponent implements OnInit {
  actionsDisabled = true;
  originalModel: Visualizations = { ...initialState.panels.visualizations };
  tempModel: Visualizations = { ...initialState.panels.visualizations };

  PANEL_NAMES = {
    variation: "Show Variation",
    highLow: "High / Low",
    surprise: "Surprise",
    revisions: "Revisions",
  };

  pageNavParams: PageParams;

  constructor(
    private appService: AppService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    window.addEventListener("MEDataSet", (rpObj: any) => {
      if (
        rpObj.detail.section === "Analysis" ||
        rpObj.detail.section === "All"
      ) {
        const data = this.getPanelData(rpObj.detail.data.panels);
        if (data !== undefined) {
          this.setVisualizationsData(data);
        }
      }
    });

    this.store
      .select(stateGetter("navigationSettings", "pageParams"))
      .subscribe((pageNavParams: PageParams) => {
        this.pageNavParams = pageNavParams;
      });
  }

  setVisualizationsData = (data) => {
    this.originalModel = {
      clearDimension: data.clear_dimension,
      values: data.values,
    };
    this.tempModel = JSON.parse(JSON.stringify(this.originalModel));
    this.actionsDisabled = true;
  };

  onMinMaxClick = (
    index: number,
    increment: number,
    maxVal: number,
    minVal: number,
  ) => {
    const currVisualization = this.tempModel.values[index] as
      | Variation
      | Revisions
      | Surprise;
    const currVal = currVisualization.slider;
    let newVal = currVal + increment;
    newVal = Math.max(minVal, newVal);
    newVal = Math.min(maxVal, newVal);
    currVisualization.slider = newVal;
    this.handleModelChanges();
  };

  getPanelProperty = (vType: string, property: string) => {
    const currentPanel = this.tempModel.values.find((visualizationType) => {
      return visualizationType.name === vType;
    });

    if (!property) {
      return currentPanel;
    }

    return currentPanel[property];
  };

  onPanelExpandCollapse = (vType: string, expanded: boolean) => {
    let currentPanel = this.tempModel.values.find((visualizationType) => {
      return visualizationType.name === vType;
    });

    if (vType === this.PANEL_NAMES.highLow) {
      currentPanel = currentPanel as HighLow;
      currentPanel.selected = expanded;
    } else {
      currentPanel = currentPanel as Variation | Revisions | Surprise;
      currentPanel.selected = (expanded && currentPanel.name) || "";
    }

    this.handleModelChanges();
    this.logUserAction(vType);
  };

  onSliderChange = (vType: string, sliderVal: number) => {
    let currentPanel = this.tempModel.values.find((visualizationType) => {
      return visualizationType.name === vType;
    });

    if (vType === this.PANEL_NAMES.surprise) {
      currentPanel = currentPanel as Surprise;
      currentPanel.slider = sliderVal;
    } else {
      currentPanel = currentPanel as Variation | Revisions;
      currentPanel.slider = -9 + sliderVal * 2;
    }

    if (vType !== "Surprise") {
      let cellList: any = [];
      if (vType === "Revisions") {
        cellList = document.getElementsByClassName("dummyForSliderRevision");
      } else {
        cellList = document.getElementsByClassName("dummyForSliderVariation");
      }

      for (let i = 0; i < cellList.length; i++) {
        variationOrRevisionSliderMoved(cellList[i], currentPanel.slider);
      }
    } else {
      const cellList = document.getElementsByClassName("surprise-text-color");
      for (let i = 0; i < cellList.length; i++) {
        const divisionFactor = getDivisionFactorForSurprise(
          currentPanel.slider,
        );
        const surpriseValue = cellList[i].getAttribute("surprise");
        surpriseColoring(cellList[i], surpriseValue, divisionFactor, true);
      }
    }

    //this.handleModelChanges();
  };

  onHighLowCheckChange = (index: number, checked: boolean) => {
    let currentPanel = this.tempModel.values.find((visualizationType) => {
      return visualizationType.name === this.PANEL_NAMES.highLow;
    });

    currentPanel = currentPanel as HighLow;
    currentPanel.values[index].selected = checked;

    highLowFilter(currentPanel.values[index].name, checked);
    // this.handleModelChanges();
  };

  handleModelChanges = () => {
    this.actionsDisabled = this.appService.compare(
      this.tempModel,
      this.originalModel,
    );
  };

  handleClear = (evt: MouseEvent) => {
    //stop collapsing of visualizations section
    evt.stopImmediatePropagation();
    OnDefaultSetting("Analysis");
  };

  handleCancel = () => {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    this.setVisualizationsData(window.MEData.panels[panelIdx]);
  };

  handleApply = (evt) => {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    this.tempModel.clearDimension = true;
    this.updateMEData(this.tempModel);
    this.setVisualizationsData(window.MEData.panels[panelIdx]);
    gridRefresh(evt.currentTarget, evt);
  };

  updateMEData(visualizations: Visualizations) {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    window.MEData.panels[panelIdx] = {
      ...window.MEData.panels[panelIdx],
      clear_dimension: visualizations.clearDimension,
      values: visualizations.values,
    };

    window.rightPanelChanges.panels[panelIdx] = window.MEData.panels[panelIdx];
  }

  getPanelData(panelsList) {
    return panelsList.find((panel) => panel.name.toLowerCase() === "analysis");
  }

  getIndexForPanel(panelsList) {
    return panelsList.findIndex(
      (panel) => panel.name.toLowerCase() === "analysis",
    );
  }

  getPanelDataFrom(panels, panelName) {
    return panels.find(
      (panel) => panel.name.toLowerCase() === panelName.toLowerCase(),
    );
  }

  logUserAction(panelName) {
    const analysisTabNames = {
      "Show Variation": "VARIATION",
      "High / Low": "HIGH-LOW",
      Surprise: "SURPRISE",
      Revisions: "REVISIONDEV",
    };

    const sliderKeys = {
      "Show Variation": "variationSliderValue",
      "High / Low": "",
      Surprise: "surpriseSliderValue",
      Revisions: "revisionSliderValue",
    };

    const currentPanel: any = this.tempModel.values.find(
      (v) => v.name === panelName,
    );
    const adjustments = this.getPanelDataFrom(
      window.MEData.panels,
      "adjustments",
    );
    const growthtypes = this.getPanelDataFrom(
      window.MEData.panels,
      "growthtypes",
    );
    const revisions = this.getPanelDataFrom(window.MEData.panels, "revisions");
    const periods = this.getPanelDataFrom(window.MEData.panels, "periods");

    this.appService.logUserAction(
      {},
      this.pageNavParams.ticker,
      this.pageNavParams.cut,
      "OPTIONS_ANALYSIS",
      {
        analysistab: analysisTabNames[panelName],
        path: window.location.toString(),
        tab: this.pageNavParams.pg,
        ...(sliderKeys[panelName] && {
          [sliderKeys[panelName]]: currentPanel.slider,
        }),
        dimension: {
          Adjustments: {
            values: adjustments && adjustments.selected,
          },
          GrowthTypes: {
            values: growthtypes.values,
          },
          Revisions: {
            values: revisions.values,
            changed_date: revisions.changed_date,
            type: revisions.type,
            revision_date_list: revisions.revision_date_list,
          },
          periods: {
            first_estimated_year: periods.first_estimated_year,
            interim_type: periods.interim_type,
            multi_period: periods.multi_period,
            period_type: periods.period_type,
            selected_from: periods.selected_from,
            selected_to: periods.selected_to,
          },
        },
      },
      "",
      null,
    );
  }
}
